import { DrawerForm, DrawerSizeEnum } from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { InputDate } from '@/components/FormFields/InputDate';
import { InputNumber } from '@/components/FormFields/InputNumber';
import { InputSchedules } from '@/components/FormFields/InputSchedules';
import { InputSwitch } from '@/components/FormFields/InputSwitch';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { InfoFormFieldHeader } from '@/components/InfoFormFieldHeader/InforFormFieldHeader';
import { useChannelOptions } from '@/features/broadcasters/hooks/useChannelOptions';
import { useProductionCenterOptions } from '@/features/production-center/hooks/useProductionCenterOptions';
import { DrawerFormDataType, FormTypeEnum, State } from '@/types';

import {
  DEFAULT_VALUES,
  useChannelRecordingDrawerFormState,
} from '../../hooks/useChannelRecordingDrawerFormState';
import { ChannelRecordingType, TChannelRecordingForm } from '../../types';

type ChannelRecordingsDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<ChannelRecordingType> | null;
  onClose: () => void;
  onSubmit: (data: TChannelRecordingForm) => void;
  isMutating: boolean;
};

export function ChannelRecordingsDrawerForm({
  isOpen,
  data,
  onClose,
  onSubmit,
  isMutating,
}: ChannelRecordingsDrawerFormProps) {
  const { t } = useTranslation();
  const { form } = useChannelRecordingDrawerFormState();

  useEffect(() => {
    if (!isOpen) {
      form.reset(DEFAULT_VALUES);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!data?.data) return;

    const formData: Partial<TChannelRecordingForm> = {
      ...data.data,
      description: data.data.description ?? '',
      channel_id: data.data.channel_id ?? '',
      start_datetime: new Date(data.data.start_datetime),
      end_datetime: data.data.end_datetime ? new Date(data.data.end_datetime) : null,
      production_center_id: data.data.production_centers[0],
      schedule: data.data.schedule,
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }

    if (data.type === FormTypeEnum.DUPLICATE) {
      form.reset(formData);
    }
  }, [data]);

  const handleSubmit = form.handleSubmit(data => {
    onSubmit({
      ...data,
    });
  });

  const productionCenterOptions = useProductionCenterOptions();
  const channelOptions = useChannelOptions({
    isParentChannel: false,
  });

  return (
    <DrawerForm
      title={
        data?.type === FormTypeEnum.CREATE
          ? t('Crear nueva programación de grabación')
          : data?.type === FormTypeEnum.UPDATE
            ? t('Editar programación de grabación')
            : t('Duplicar programación de grabación')
      }
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
      isMutating={isMutating}
    >
      <InputSwitch
        form={form}
        name="state"
        states={{
          active: State.Active,
          inactive: State.Inactive,
        }}
      />

      <InputText
        form={form}
        name="description"
        label={t('Descripción')}
        placeholder={t('Escribe aquí la descripción')}
        required
      />

      <SelectForm
        form={form}
        name="channel_id"
        label={t('Emisora')}
        placeholder={t('Seleccionar...')}
        options={channelOptions}
        required
      />

      <RowContainer>
        <SelectForm
          form={form}
          name="production_center_id"
          label={t('Centro de producción')}
          placeholder={t('Seleccionar...')}
          options={productionCenterOptions}
          required
        />

        <div className="w-[165px]">
          <InputNumber form={form} name="segment_duration" label={t('Duración franja')} />
        </div>
      </RowContainer>

      <RowContainer>
        <InputDate
          form={form}
          name="start_datetime"
          label={t('Fecha inicio')}
          showTimeField={false}
          required
        />

        <InputDate form={form} name="end_datetime" label={t('Fecha fin')} showTimeField={false} />
      </RowContainer>

      <InfoFormFieldHeader
        title={t('Programaciones')}
        tooltip={t('Programa varias grabaciones dentro del rango de fechas establecido')}
      />

      <InputSchedules form={form} name="schedule" />
    </DrawerForm>
  );
}
